import { memo } from 'react';
import {
  useGetGlobalHierarchyFiltersQuery,
  useHierarchyTypesQuery,
  useHierarchyValuesQuery,
} from '../../api/enhanced/enhanced-v4-api';
import { groupSelectedByType } from '../../helpers/global-hierarchy';
import { formatSelectedValues } from '../../helpers/hierarchy-display';

// Show the first few value captions inline; collapse the rest into a "+N" with the full list on hover.
const MAX_VALUES_SHOWN = 2;

/** Resolves the selected value ids for one hierarchy type into their captions (or a count fallback). */
function HierarchyTypeValues({ typeId, valueIds }: { typeId: number; valueIds: number[] }) {
  const { data: values = [] } = useHierarchyValuesQuery({ typeId });
  const { text, title } = formatSelectedValues(valueIds, values, MAX_VALUES_SHOWN);

  return <span title={title || undefined}>{text}</span>;
}

/**
 * Header indicator showing the user's active global-hierarchy scope as the selected value captions
 * (e.g. "Global Hierarchy: Site A"), or "All" when nothing is scoped. Reuses the MYOSH-14079 queries.
 *
 * POC: single-level hierarchy only — no multi-level tree path (deferred to the 14084 unification).
 */
function HierarchyScopeIndicator() {
  const { data: types = [] } = useHierarchyTypesQuery();
  const { data: savedScope, isLoading: isScopeLoading } = useGetGlobalHierarchyFiltersQuery();

  // Nothing to show on tenants without a global hierarchy, or until the saved scope resolves
  // (avoids briefly rendering "All" before it loads).
  if (types.length === 0 || isScopeLoading) {
    return null;
  }

  const selectedByType = groupSelectedByType(savedScope);
  // Order by the hierarchy types' configured order rather than numeric id order.
  const typeIds = types.map((type) => type.id).filter((id) => (selectedByType[id]?.length ?? 0) > 0);
  const scoped = typeIds.length > 0;

  return (
    <div data-cy="hierarchy-scope-indicator" className="text-gray-3 flex items-center gap-1 whitespace-nowrap text-sm">
      <span className="font-medium">Global Hierarchy:</span>
      {scoped ? (
        typeIds.map((typeId, index) => (
          <span key={typeId} className="flex items-center gap-1">
            {index > 0 && <span>·</span>}
            <HierarchyTypeValues typeId={typeId} valueIds={selectedByType[typeId]} />
          </span>
        ))
      ) : (
        <span>All</span>
      )}
    </div>
  );
}

export default memo(HierarchyScopeIndicator);
